/**
 * FASE 5 / BLOCO BA-4 — BI 9: assinatura do desafio no NAVEGADOR.
 *
 * Módulo puro (sem Vue, sem DOM) para poder ser testado sem navegador — mesma decisão do
 * `biInteracao.js` no BA-2.
 *
 * O QUE ESTE MÓDULO FAZ, e por que importa:
 *
 * A senha do `.pfx` e a CHAVE PRIVADA nunca saem da máquina do usuário. O que trafega é
 * apenas (a) o certificado público e (b) a assinatura do nonce emitido pelo servidor. Enviar
 * o `.pfx` para o backend "para ele assinar" seria entregar a identidade digital do servidor
 * público a um servidor — inaceitável, e é a implementação ingênua que se vê por aí.
 *
 * A validação (assinatura, cadeia, validade, CPF, vínculo) é toda do backend
 * (`EcpfAutenticacaoService`). Aqui só se produz a prova de posse.
 */

/** Converte PKCS#12 (.pfx) em {certificadoPem, chavePrivada} usando node-forge. */
function abrirPfx (forge, bufferPfx, senha) {
  const asn1 = forge.asn1.fromDer(forge.util.createBuffer(bufferPfx))
  const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, false, senha)

  let certificado = null
  let chave = null

  p12.safeContents.forEach(function (conteudo) {
    conteudo.safeBags.forEach(function (bag) {
      if (bag.type === forge.pki.oids.certBag && bag.cert && !certificado) {
        // O certificado do titular é o que tem a extensão de uso de cliente; na prática,
        // o primeiro com chave associada. Guardamos o primeiro e refinamos abaixo.
        certificado = bag.cert
      }
      if ((bag.type === forge.pki.oids.pkcs8ShroudedKeyBag ||
           bag.type === forge.pki.oids.keyBag) && bag.key && !chave) {
        chave = bag.key
      }
    })
  })

  if (!certificado) throw new Error('O arquivo não contém certificado.')
  if (!chave) throw new Error('O arquivo não contém chave privada (senha incorreta?).')

  return {
    certificadoPem: forge.pki.certificateToPem(certificado),
    chavePrivada: chave,
    certificado: certificado
  }
}

/** Assina o nonce com SHA-256/RSA e devolve base64 — o formato que `openssl_verify` espera. */
function assinarNonce (forge, chavePrivada, nonce) {
  const md = forge.md.sha256.create()
  md.update(nonce, 'utf8')
  const assinatura = chavePrivada.sign(md)
  return forge.util.encode64(assinatura)
}

/**
 * Fluxo completo do lado do cliente.
 *
 * @param deps  { forge, obterDesafio(), autenticar(nonce, assinatura, certificadoPem) }
 */
function autenticarComPfx (deps, bufferPfx, senha) {
  const forge = deps.forge

  let material
  try {
    material = abrirPfx(forge, bufferPfx, senha)
  } catch (e) {
    return Promise.resolve({
      autenticado: false,
      motivo: 'pfx_ilegivel',
      mensagem: 'Não foi possível abrir o certificado. Verifique o arquivo e a senha.'
    })
  }

  return deps.obterDesafio().then(function (desafio) {
    if (!desafio || !desafio.nonce) {
      return { autenticado: false, motivo: 'sem_desafio',
               mensagem: 'O servidor não emitiu o desafio.' }
    }

    const assinatura = assinarNonce(forge, material.chavePrivada, desafio.nonce)

    return deps.autenticar(desafio.nonce, assinatura, material.certificadoPem)
  })
}

/** Mensagens dos motivos que o backend devolve — em português, para o operador. */
const MENSAGENS = {
  desafio_inexistente: 'Desafio inválido. Tente novamente.',
  desafio_ja_consumido: 'Este desafio já foi utilizado. Tente novamente.',
  desafio_expirado: 'O desafio expirou. Tente novamente.',
  certificado_ilegivel: 'O certificado apresentado não pôde ser lido.',
  certificado_ainda_nao_valido: 'O certificado ainda não é válido.',
  certificado_expirado: 'O certificado está vencido.',
  chave_publica_ilegivel: 'A chave pública do certificado não pôde ser lida.',
  assinatura_invalida: 'A assinatura não confere com o certificado apresentado.',
  cadeia_nao_verificavel: 'A cadeia de confiança não pôde ser verificada no servidor.',
  ca_bundle_ausente: 'A cadeia da ICP-Brasil não está configurada no servidor.',
  cadeia_nao_confiavel: 'O certificado não pertence a uma cadeia confiável.',
  cpf_ausente_no_certificado: 'Não foi possível extrair o CPF do certificado.',
  cpf_sem_usuario: 'Não há usuário cadastrado para o CPF deste certificado.',
  usuario_inativo: 'O usuário deste CPF está inativo.',
  pfx_ilegivel: 'Não foi possível abrir o certificado. Verifique o arquivo e a senha.',
  sem_desafio: 'O servidor não emitiu o desafio.'
}

function mensagemDoMotivo (motivo) {
  return MENSAGENS[motivo] || 'Autenticação recusada.'
}

export { abrirPfx, assinarNonce, autenticarComPfx, mensagemDoMotivo, MENSAGENS }
